import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PairService } from '../../../services/pair.service';
import { Pair } from '../../../interfaces/pair';
import { MatchingPeriod } from '../../../interfaces/matchingPeriod';
import { ParticipantService } from '../../../services/participant.service';
import { Subject } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Donor } from '../../../interfaces/donor';
import { Recipient } from '../../../interfaces/recipient';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatchingPeriodService } from '../../../services/matchingPeriod.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { Participant } from '../../../interfaces/participant';
import { AuthService } from '../../../auth/auth.service';
@Component({
  selector: 'app-pairs',
  imports: [ReactiveFormsModule, MatIconModule, MatInputModule, MatSelectModule, MatCheckboxModule, MatButtonModule, MatFormFieldModule, CommonModule, MatDialogModule, MatAutocompleteModule],
  templateUrl: './pair-add.component.html',
  styleUrl: './pair-add.component.scss'
})
export class PairAddComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private pairService: PairService,
    private matchingPeriodService: MatchingPeriodService,
    private participantService: ParticipantService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<PairAddComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: Participant // Inject the passed participant
  ) {}

  relations: string[] = ['Parent', 'Child', 'Partner', 'Sibling', 'Grandparent', 'Grandchild', 'Nephew', 'Niece', 'Uncle', 'Aunt', 'FamilyNotRelated', 'DonorByMedia', 'NotRelated'];

  pairForm!: FormGroup;
  donorSearchControl = new FormControl<string | Donor | null>('');
  recipientSearchControl = new FormControl<string | Recipient | null>('');
  pairs: Pair[] = [];
  donors: Donor[] = [];
  recipients: Recipient[] = [];
  currentMatchingPeriod?: MatchingPeriod;
  filteredDonors = this.donors;
  filteredRecipients = this.recipients;
  selectedDonor: any = null;
  selectedRecipient: any = null;
  isCenterUser: boolean = false;
  hasMatchrunStarted: boolean = false;

  //Send back new pair to list
  responseSubjectNewPair: Subject<Pair> = new Subject<Pair>();

  ngOnInit(): void {
    this.pairForm = this.fb.group({
      donor: [null, Validators.required],
      recipient: [null, Validators.required],
      compatible: [false],
      comments: [null],
      addPairToMatchingPeriod: [true],
      active: [true],
      relationToRecipient: ['', Validators.required]
    });
    this.loadPairsAndParticipants();
    this.checkUserRoleAndMatchrunStatus();

    // Pre-select donor or recipient if provided
    if (this.data) {
      if (this.data.type === 'Donor') {
        this.selectDonor(this.data as Donor); // Call selectDonor to ensure all logic is executed
        this.pairForm.get('donor')?.disable(); // Make the recipient field read-only
      } else if (this.data.type === 'Recipient') {
        this.selectRecipient(this.data as Recipient); // Call selectRecipient to ensure all logic is executed
        this.pairForm.get('recipient')?.disable(); // Make the recipient field read-only
      }
    }

    this.donorSearchControl.valueChanges.subscribe((value) => {
      if (value !== null && typeof value === 'object') {
        this.selectDonor(value);
      }
    });

    this.recipientSearchControl.valueChanges.subscribe((value) => {
      if (value !== null && typeof value === 'object') {
        this.selectRecipient(value);
      }
    });
  }

  async checkUserRoleAndMatchrunStatus() {
    // Check user role
    this.authService.getUser().subscribe((user) => {
      this.isCenterUser = user.activeRole.enumName === 'CENTER';

      // Check if matchrun has started
      this.matchingPeriodService.getLatestMatchingPeriod().subscribe((currentMatchingPeriodResult) => {
        if (currentMatchingPeriodResult != null) {
          this.currentMatchingPeriod = currentMatchingPeriodResult;
          this.hasMatchrunStarted = !!(currentMatchingPeriodResult.matchRuns && currentMatchingPeriodResult.matchRuns.length > 0);

          // If CENTER user and matchrun has started, disable addPairToMatchingPeriod
          if (this.isCenterUser && this.hasMatchrunStarted) {
            this.pairForm.get('addPairToMatchingPeriod')?.setValue(false);
            this.pairForm.get('addPairToMatchingPeriod')?.disable();
            this.showNotification('New pairs added after a matchrun has started will be set as inactive and awaiting the next match run period.');
          }
        }
      });
    });
  }

  private async loadPairsAndParticipants(): Promise<void> {
    // Load pairs first
    await this.loadPairs();

    // Then load and filter participants
    this.loadParticipants();
  }

  private async loadPairs(): Promise<void> {
    return new Promise((resolve) => {
      this.pairService.getAllPairs().subscribe((pairs) => {
        this.pairs = pairs;
        resolve();
      });
    });
  }

  private loadParticipants(): void {
    this.participantService.getAllParticipants().subscribe((participants) => {
      // Extract paired donor and recipient IDs from pairs
      const pairedDonorIds = new Set(this.pairs.map((pair) => pair.donor.id));
      const pairedRecipientIds = new Set(
        this.pairs
          .filter((pair) => pair.recipient !== null) // Only include non-null recipient IDs
          .map((pair) => pair.recipient.id)
      );

      // Create a map of recipient IDs to their linked donors
      const recipientToDonorsMap = new Map<string, Donor[]>();
      this.pairs.forEach((pair) => {
        if (pair.recipient) {
          const recipientId = pair.recipient.id;
          if (recipientId) {
            if (!recipientToDonorsMap.has(recipientId)) {
              recipientToDonorsMap.set(recipientId, []);
            }
            recipientToDonorsMap.get(recipientId)?.push(pair.donor);
          }
        }
      });

      // Filter participants based on pairings
      participants.forEach((participant) => {
        if (
          participant.type === 'Donor' &&
          !pairedDonorIds.has(participant.id) &&
          (participant as Donor).altruist !== true // Check if donor.altruist is true
        ) {
          this.donors.push(participant as Donor);
        } else if (participant.type === 'Recipient') {
          const recipient = participant as Recipient;
          let linkedDonors: Donor[] = [];
          if (recipient.id) linkedDonors = recipientToDonorsMap.get(recipient.id) || [];

          // Allow up to 3 donors per recipient, but exclude postmortem waitlist recipients
          if (linkedDonors.length < 3 && recipient.postmortumwaitlist !== true) {
            this.recipients.push(recipient);
          }
        }
      });
    });
  }

  onSubmit(): void {
    if (this.pairForm.valid) {
      // Requested state only. The backend (PairStateService) decides the final state of the
      // pair and both participants and adds the pair to the current matching period in the
      // same request - the client must not follow up with a second call that would overwrite
      // that decision (CROS-398).
      this.pairForm.value.active = this.pairForm.value.addPairToMatchingPeriod === true;

      // Retrieve the full donor and recipient objects
      let donor = this.donors.find((d) => d.id === this.pairForm.value.donor);
      let recipient = this.recipients.find((r) => r.id === this.pairForm.value.recipient);

      if (!donor && this.data) {
        // If donor is not found, use the injected data
        donor = this.data as Donor;
      }
      if (!recipient && this.data) {
        // If recipient is not found, use the injected data
        recipient = this.data as Recipient;
      }
      if (!donor || !recipient) {
        this.showNotification('Invalid donor or recipient selection.');
        return;
      }

      // Replace donor and recipient IDs with full objects
      const pairData = {
        ...this.pairForm.value,
        donor: donor,
        recipient: recipient,
        addToMatchingPeriod: this.pairForm.value.addPairToMatchingPeriod === true
      };

      this.pairService.createPair(pairData).subscribe({
        next: (response) => {
          console.log('Pair created:', response);

          if (response.active === false && this.pairForm.value.addPairToMatchingPeriod === true) {
            // A participant was inactive, so the pair was created inactive and is not part of
            // the current matching period yet.
            this.showNotification(
              response.reasonInactive
                ? `Pair created as inactive (${response.reasonInactive}) because a participant is inactive.`
                : 'Pair created as inactive because a participant is inactive.'
            );
          } else {
            this.showNotification('Pair created successfully!');
          }

          // Remove the paired donor and recipient from the lists
          this.donors = this.donors.filter((d) => d.id !== donor.id);
          this.recipients = this.recipients.filter((r) => r.id !== recipient.id);

          this.responseSubjectNewPair.next(response);

          this.pairForm.reset();
          this.dialogRef.close(response);
        },
        error: (err) => {
          console.error('Error creating pair:', err);
          this.showNotification('Failed to create pair. Please try again.');
        }
      });
    } else {
      this.showNotification('Please fill out all required fields correctly.');
    }
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  filterDonors() {
    const val = this.donorSearchControl.value;
    const searchValue = typeof val === 'string' ? val.toLowerCase() : '';
    this.filteredDonors = this.donors.filter((donor) => `${donor.enisNumber}`.toString().includes(searchValue));
  }

  filterRecipients() {
    const val = this.recipientSearchControl.value;
    const searchValue = typeof val === 'string' ? val.toLowerCase() : '';
    this.filteredRecipients = this.recipients.filter(
      (recipient) => `${recipient.enisNumber}`.toString().includes(searchValue) && recipient.postmortumwaitlist !== true
    );
  }

  selectDonor(donor: Donor) {
    this.selectedDonor = donor;
    this.donorSearchControl.setValue(this.displayDonorName(donor));
    this.pairForm.get('donor')?.setValue(donor.id); // Set selected donor ID in the form
    console.log(donor);
  }

  selectRecipient(recipient: Recipient) {
    this.selectedRecipient = recipient;
    this.recipientSearchControl.setValue(this.displayRecipientName(recipient));
    this.pairForm.get('recipient')?.setValue(recipient.id); // Set selected recipient ID in the form
  }

  clearDonorSelection(event: Event) {
    this.selectedDonor = null;
    this.donorSearchControl.setValue('');
    this.pairForm.get('donor')?.reset();
    this.filteredDonors = this.donors;
  }

  clearRecipientSelection(event: Event) {
    this.selectedRecipient = null;
    this.recipientSearchControl.setValue('');
    this.pairForm.get('recipient')?.reset();
    this.filteredRecipients = this.recipients;
  }

  displayDonorName(donor: Donor | string | null): string {
    if (typeof donor === 'string') {
      return donor;
    }
    if (donor) {
      if (donor.firstName || donor.lastName) {
        return `${donor.enisNumber} - ${donor.firstName || ''} ${donor.lastName || ''}`.trim();
      }
      return `${donor.enisNumber} - ${donor.dob}`;
    }
    return '';
  }

  displayRecipientName(recipient: Recipient | string | null): string {
    if (typeof recipient === 'string') {
      return recipient;
    }
    if (recipient) {
      if (recipient.firstName || recipient.lastName) {
        return `${recipient.enisNumber} - ${recipient.firstName || ''} ${recipient.lastName || ''}`.trim();
      }
      return `${recipient.enisNumber} - ${recipient.dob}`;
    }
    return '';
  }
}
